<template>
    <v-card flat class="mt-n2" color="transparent">
      <!-- Top Action Bar: Add Button & Search Input -->
      <v-row dense align="center" class="mb-2">
        <v-col :cols="12">
          <v-text-field
            v-model="search"
            :label="`Search ${label}`"
            prepend-inner-icon="mdi-magnify"
            filled
            flat
            dense
            clearable
            hide-details
          ></v-text-field>
        </v-col>
        <v-col :cols="12" v-if="isAdmin">
          <v-btn color="primary" block dense small @click="openDialog('add')">
            <v-icon dark left>mdi-plus-circle</v-icon>
            Add New {{ formatName(table) }}
          </v-btn>
        </v-col>
      </v-row>
  
      <!-- Virtual Scroll List -->
      <v-card outlined v-if="filteredItems.length > 0">
        <v-virtual-scroll
          :items="filteredItems"
          :height="scrollContainerHeight"
          item-height="64"
          bench="5"
        >
          <template v-slot:default="{ item }">
            <v-list-item :key="item.id" class="px-2">
              <!-- Large Radio Button Selection -->
              <v-list-item-action class="mr-2">
                <v-checkbox
                :input-value="selectedId === item.id"
                color="success"
                on-icon="mdi-radiobox-marked"
                off-icon="mdi-radiobox-blank"
                class="ma-0 pa-0"
                hide-details
                @click.prevent="selectItem(item)"
                ></v-checkbox>
              </v-list-item-action>
  
              <!-- Line Content & Sub-Text -->
              <v-list-item-content class="py-1 cursor-pointer" @click="selectItem(item)">
                  {{ item.name }}
                <v-list-item-subtitle v-if="getSubtext(item)" class="grey--text text--darken-1 text-wrap">
                  {{ getSubtext(item) }}
                </v-list-item-subtitle>
              </v-list-item-content>
  
              <!-- Action Buttons (Edit / Delete) -->
              <v-list-item-action class="flex-row ma-0" v-if="isAdmin">
                <v-btn icon small color="primary" @click.stop="openDialog('edit', item)">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon small color="error" @click.stop="openDialog('delete', item)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
            <v-divider></v-divider>
          </template>
        </v-virtual-scroll>
      </v-card>
  
      <!-- Empty State -->
      <div v-else class="mt-2">
        <v-card :style="{background:getThemeColor}" dark>
        <v-card-text>
          Loading...
          <v-progress-linear
            :indeterminate="loading"
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
      </div>
  
      <!-- CRUD Dialog -->
      <v-dialog v-model="dialog" max-width="500px" persistent>
        <v-card>
          <v-card-title class="headline grey lighten-2 uppercase">
            {{ mode }} {{ table }}
          </v-card-title>
  
          <v-card-text class="mt-4">
            <v-container v-if="mode !== 'delete'">
              <v-row>
                <v-col v-for="col in columns" :key="col" cols="12">
                  <AutoCompleteCrudAdvanced
                    v-if="relations[col]"
                    v-model="editedItem[col]"
                    :table="relations[col].relationTable"
                    :label="formatLabel(col)"
                    @input="(val) => { editedItem[col] = val ? val.id : null }"
                  ></AutoCompleteCrudAdvanced>
  
                  <v-menu
                    v-else-if="isDateColumn(col)"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="editedItem[col]"
                        :label="formatLabel(col)"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="editedItem[col]" no-title scrollable></v-date-picker>
                  </v-menu>
  
                  <template v-else-if="isColorColumn(col)">
                    <p class="mb-1 grey--text text--darken-1">{{ formatLabel(col) }}</p>
                    <v-color-picker
                      v-model="editedItem[col]"
                      flat
                      dot-size="25"
                      swatches-max-height="200"
                      class="mb-4"
                    ></v-color-picker>
                  </template>
  
                  <v-text-field
                    v-else
                    v-model="editedItem[col]"
                    :label="formatLabel(col)"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
  
            <v-alert v-else type="warning" text>
              Are you sure you want to delete this record?
            </v-alert>
          </v-card-text>
  
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="dialog = false">Cancel</v-btn>
            <v-btn :color="mode === 'delete' ? 'error' : 'success'" depressed :loading="saving" @click="save">
              {{ mode === 'delete' ? 'Confirm Delete' : 'Save Changes' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </template>
  
  <script>
  import axios from "@/plugins/axios"
  import helpersJs from "@/plugins/helpers"
  import AutoCompleteCrudAdvanced from "@/modules/CrudDatabases/AutoCompleteCrudAdvanced.vue"
  
  export default {
    name: "VirtualScrollCrudAdvanced",
    components:{
      AutoCompleteCrudAdvanced
    },
    props: {
      triggerRefresh: { type: Number, default: 0 },
      value: [String, Number, Object],
      table: { type: String, required: true },
      label: { type: String, default: "Search Items..." },
      icon: { type: String, default: "mdi-magnify" },
      formatter: { type: Function, default: null }
    },
    data: () => ({
      search: '',
      items: [],
      columns: [],
      relations: {},
      loading: false,
      saving: false,
      dialog: false,
      mode: 'add',
      selectedId: null,
      editedItem: {},
    }),
    computed: {
      getAuthenticated() { return this.$store.getters.getAuthenticated },
      getAccessToken() { return this.$store.getters.getAccessToken },
      getThemeColor() { return this.$store.getters.getThemeColor },
      isMobile() { return this.$store.getters.isMobile },
      isAdmin() { return true },
      
      // Calculates list container height to ensure at least 10 items fit (64px item height)
      scrollContainerHeight() {
        const visibleCount = Math.min(this.filteredItems.length, 5);
        return (visibleCount > 0 ? visibleCount : 1) * 64;
      },
  
      // Search filter across item name and computed subtext
      filteredItems() {
        if (!this.search) return this.items;
        const query = this.search.toLowerCase();
        
        return this.items.filter(item => {
          const nameMatch = item.name ? String(item.name).toLowerCase().includes(query) : false;
          const subtext = this.getSubtext(item);
          const subtextMatch = subtext ? String(subtext).toLowerCase().includes(query) : false;
          return nameMatch || subtextMatch;
        });
      }
    },
    watch: {
      triggerRefresh: {
        handler() { this.initializeComponent() },
        immediate: true
      },
      value: {
        handler(val) { 
          this.selectedId = (val && typeof val === 'object') ? val.id : val;
        },
        immediate: true
      },
      table: {
        handler() { this.initializeComponent() },
        immediate: true
      }
    },
    async mounted(){
      await this.initializeComponent() 
    },
    methods: {
      async initializeComponent() {
        
        await this.fetchItems();
        this.$store.commit("setScreenWidth", this.$vuetify.breakpoint.width);
      },
  
      formatName(name) {
        return helpersJs.formatUnderscoreNamesWithFirstCapitalLetters(name)
      },
  
      async fetchItems() {
        if (!this.getAuthenticated || !this.getAccessToken) return;
  
        this.loading = true;
        try {
          const res = await axios.postDataWithToken("/auth/get-table-items-with-table-name", {
            table: this.table
          }, this.getAccessToken);
  
          if (res && res.status === "success") {
            this.items = this.formatter ? this.formatter(res.items) : res.items;
            this.columns = res.columns;
            this.relations = res.relations || {};
          }
        } catch (e) {
          console.error("Fetch failed", e);
        } finally {
          this.loading = false;
        }
      },
  
      isDateColumn(col) {
        const datesColumns = ['birthday', 'dob', 'expiry', 'start', 'end'];
        const column = col.toLowerCase();
        return column.includes('date') || datesColumns.includes(column);
      },
  
      isColorColumn(col) {
        return col.toLowerCase().includes('color');
      },
  
      formatLabel(col) {
        if (col === "dob") return "Date of Birth";
        return col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      },
  
      getSubtext(item) {
        let secondaryCol = '';
        if (item && (item['make'] || item['model'])) {
          return `${item['make'] || ''} ${item['model'] || ''} - ${item['year'] || ''} | ${item['license_plate'] || ''}`;
        } else if (item && item['description']) {
          secondaryCol = 'description';
        } else if (item && item['institution']) {
          secondaryCol = 'institution';
        } else {
          secondaryCol = this.columns.find(c => c !== 'name' && c !== 'id');
        }
        
        if (!secondaryCol || !this.relations[secondaryCol]) return item[secondaryCol] || '';
  
        const relatedList = this.relations[secondaryCol].items;
        const relatedItem = relatedList ? relatedList.find(r => r.id === item[secondaryCol]) : null;
        
        return relatedItem ? relatedItem.name : item[secondaryCol];
      },
  
      selectItem(item) {
        this.selectedId = item.id;
        this.$emit('input', item);
      },
  
      openDialog(mode, item = null) {
        this.mode = mode;
        if (item) {
          this.editedItem = { ...item };
        } else {
          let newItem = {};
          this.columns.forEach(col => newItem[col] = '');
          this.editedItem = newItem;
        }
        this.dialog = true;
      },
  
      async save() {
        this.saving = true;
        try {
          const dataToSave = { ...this.editedItem };
  
          Object.keys(dataToSave).forEach(key => {
            if (this.isColorColumn(key) && typeof dataToSave[key] === 'object') {
              dataToSave[key] = dataToSave[key].hex;
            }
          });
  
          const res = await axios.postDataWithToken(`/auth/add-edit-delete-autocomplete`, {
            ...dataToSave,
            table: this.table,
            mode: this.mode
          }, this.getAccessToken);
  
          if (res && res.status === 'success') {
            await this.fetchItems();
            this.dialog = false;
  
            if (this.mode === 'delete' && this.selectedId === this.editedItem.id) {
              this.selectedId = null;
              this.$emit('input', null);
            }
          
            if (this.mode === 'add') {  
              const newId = res?.data?.id || null;
              if (newId) {
                const newlyCreated = this.items.find(i => i.id === newId);
                if (newlyCreated) this.selectItem(newlyCreated);
              }
            }
          }
        } finally {
          this.saving = false;
        }
      }
    }
  }
  </script>
  
  <style scoped>
  .transform-scale-125 {
    transform: scale(1.25);
  }
  .cursor-pointer {
    cursor: pointer;
  }
  </style>